import QRCode from 'qrcode';

/**
 * Renders synthetic A4 "form-like" pages on a canvas with an optional QR code
 * baked into the bottom-end corner. The QR shape and position mirror what the
 * existing photo-scanning injector produces, so any page generated here flows
 * through the real upload + classify path unchanged.
 *
 * Output is always JPEG at 92% quality — matches the format the scanner
 * uploads emit and keeps file sizes small for tests with 50+ pages.
 */

export type QrMode = 'match' | 'mismatch' | 'none';

export interface GenerateOptions {
  containerNumber: string;
  pageCount: number;
  qrMode: QrMode;
  /** A4 width in physical pixels. Derived from DPI on the call site. */
  widthPx: number;
  /** A4 height in physical pixels. */
  heightPx: number;
  /** Optional progress callback so the UI can render a determinate bar — 0..1. */
  onProgress?: (fraction: number) => void;
}

export interface GeneratedPage {
  pageNumber: number;
  blob: Blob;
  qrPayload: string | null;
}

const A4_MM = { width: 210, height: 297 } as const;

/** Convert a DPI (dots per inch) to A4 pixel dimensions. */
export function a4Dimensions(dpi: number): { width: number; height: number } {
  const mmToInch = 1 / 25.4;
  return {
    width: Math.round(A4_MM.width * mmToInch * dpi),
    height: Math.round(A4_MM.height * mmToInch * dpi)
  };
}

export async function generateContainerPages(
  opts: GenerateOptions
): Promise<GeneratedPage[]> {
  const pages: GeneratedPage[] = [];
  for (let n = 1; n <= opts.pageCount; n++) {
    const rendered = await renderOnePage(opts, n);
    pages.push({ pageNumber: n, blob: rendered.blob, qrPayload: rendered.qrPayload });
    opts.onProgress?.(n / opts.pageCount);
  }
  return pages;
}

async function renderOnePage(
  opts: GenerateOptions,
  pageNumber: number
): Promise<{ blob: Blob; qrPayload: string | null }> {
  const canvas = document.createElement('canvas');
  canvas.width = opts.widthPx;
  canvas.height = opts.heightPx;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context is not available in this browser');
  }

  // ── Background + frame ────────────────────────────────────────────────
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const margin = Math.round(canvas.width * 0.05);
  ctx.strokeStyle = '#1f2937';
  ctx.lineWidth = Math.max(2, Math.round(canvas.width * 0.003));
  ctx.strokeRect(margin, margin, canvas.width - margin * 2, canvas.height - margin * 2);

  // ── Header band ───────────────────────────────────────────────────────
  const headerHeight = Math.round(canvas.height * 0.09);
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(margin, margin, canvas.width - margin * 2, headerHeight);

  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${Math.round(canvas.width * 0.035)}px "Helvetica Neue", Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(
    'SAFHA · TEST CONTAINER PAGE',
    canvas.width / 2,
    margin + headerHeight / 2
  );

  // ── Metadata grid ─────────────────────────────────────────────────────
  ctx.fillStyle = '#0f172a';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `bold ${Math.round(canvas.width * 0.022)}px "Helvetica Neue", Arial, sans-serif`;

  const fieldX = margin + Math.round(canvas.width * 0.025);
  let cursorY = margin + headerHeight + Math.round(canvas.height * 0.04);
  const lineHeight = Math.round(canvas.height * 0.032);

  drawField(ctx, 'Container Number', opts.containerNumber, fieldX, cursorY, canvas.width);
  cursorY += lineHeight;
  drawField(
    ctx,
    'Page',
    `${pageNumber} of ${opts.pageCount}`,
    fieldX,
    cursorY,
    canvas.width
  );
  cursorY += lineHeight;
  drawField(
    ctx,
    'Generated',
    new Date().toISOString().replace('T', ' ').replace(/\..+$/, ''),
    fieldX,
    cursorY,
    canvas.width
  );
  cursorY += lineHeight;
  drawField(
    ctx,
    'QR mode',
    opts.qrMode === 'match'
      ? 'match (success expected)'
      : opts.qrMode === 'mismatch'
        ? 'mismatch (wrong container)'
        : 'none (unverified)',
    fieldX,
    cursorY,
    canvas.width
  );
  cursorY += Math.round(lineHeight * 1.5);

  // ── Filler "form" lines ───────────────────────────────────────────────
  // Synthetic ruled lines so each page visually resembles a paper document
  // the operator would scan, without leaking real customer data.
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1;
  const ruleX1 = margin + Math.round(canvas.width * 0.025);
  const ruleX2 = canvas.width - margin - Math.round(canvas.width * 0.025);
  const ruleStart = cursorY;
  const ruleEnd = canvas.height - margin - Math.round(canvas.height * 0.22);
  const ruleSpacing = Math.round(canvas.height * 0.04);
  for (let y = ruleStart; y < ruleEnd; y += ruleSpacing) {
    ctx.beginPath();
    ctx.moveTo(ruleX1, y);
    ctx.lineTo(ruleX2, y);
    ctx.stroke();
  }

  // A few short scribbles so the image isn't a totally blank A4 — easier
  // to spot misalignment / rotation issues during QA.
  ctx.fillStyle = '#475569';
  ctx.font = `${Math.round(canvas.width * 0.018)}px "Helvetica Neue", Arial, sans-serif`;
  const sampleLines = [
    `Sample notes for page ${pageNumber}`,
    `Lorem ipsum dolor sit amet, consectetur adipiscing elit`,
    `Sed do eiusmod tempor incididunt ut labore et dolore`,
    `Magna aliqua. Ut enim ad minim veniam, quis nostrud`
  ];
  sampleLines.forEach((line, i) => {
    ctx.fillText(line, ruleX1, ruleStart + ruleSpacing * (i + 1) - 6);
  });

  // ── Footer bar ────────────────────────────────────────────────────────
  ctx.fillStyle = '#94a3b8';
  ctx.font = `${Math.round(canvas.width * 0.014)}px "Helvetica Neue", Arial, sans-serif`;
  ctx.textAlign = 'left';
  ctx.fillText(
    'Generated by Safha dev-tools · for testing only',
    fieldX,
    canvas.height - margin - Math.round(canvas.height * 0.015)
  );

  // ── Optional QR ──────────────────────────────────────────────────────
  let qrPayload: string | null = null;
  if (opts.qrMode === 'match') {
    qrPayload = `${opts.containerNumber}-P${pageNumber}`;
  } else if (opts.qrMode === 'mismatch') {
    qrPayload = `OTHER-CONTAINER-P${pageNumber}`;
  }

  if (qrPayload) {
    await drawQrCode(ctx, canvas, qrPayload, margin);
  }

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
      'image/jpeg',
      0.92
    );
  });

  return { blob, qrPayload };
}

function drawField(
  ctx: CanvasRenderingContext2D,
  label: string,
  value: string,
  x: number,
  y: number,
  canvasWidth: number
): void {
  ctx.fillStyle = '#64748b';
  ctx.font = `${Math.round(canvasWidth * 0.018)}px "Helvetica Neue", Arial, sans-serif`;
  ctx.fillText(label.toUpperCase(), x, y - Math.round(canvasWidth * 0.018));

  ctx.fillStyle = '#0f172a';
  ctx.font = `bold ${Math.round(canvasWidth * 0.024)}px "Helvetica Neue", Arial, sans-serif`;
  ctx.fillText(value, x, y + Math.round(canvasWidth * 0.005));
}

async function drawQrCode(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  payload: string,
  margin: number
): Promise<void> {
  const shortSide = Math.min(canvas.width, canvas.height);
  const qrSize = Math.max(180, Math.round(shortSide * 0.18));
  const pad = Math.round(qrSize * 0.1);

  const dataUrl = await QRCode.toDataURL(payload, {
    width: qrSize,
    margin: 1,
    errorCorrectionLevel: 'M'
  });
  const img = await loadImage(dataUrl);

  const x = canvas.width - qrSize - pad - margin;
  const y = canvas.height - qrSize - pad - margin;

  // White backing so the QR has a guaranteed quiet zone even on tinted scans.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x - pad, y - pad, qrSize + pad * 2, qrSize + pad * 2);
  ctx.drawImage(img, x, y, qrSize, qrSize);

  // Caption below the QR so QA can read the embedded payload at a glance.
  ctx.fillStyle = '#0f172a';
  ctx.textAlign = 'center';
  ctx.font = `${Math.round(canvas.width * 0.014)}px "Courier New", monospace`;
  ctx.fillText(payload, x + qrSize / 2, y + qrSize + pad + Math.round(canvas.width * 0.018));
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// ─── Output helpers ──────────────────────────────────────────────────────

/**
 * Bundle every rendered page into a single A4 PDF and trigger a browser
 * download. Uses jsPDF dynamically so the lib only loads when the dev-tool is
 * actually exercised.
 */
export async function downloadAsPdf(
  pages: GeneratedPage[],
  filename: string
): Promise<void> {
  if (pages.length === 0) return;
  const { jsPDF } = await import('jspdf');

  // A4 in points — jsPDF will stretch each JPEG to fill the page regardless
  // of the source pixel resolution, so the operator sees A4-sized pages.
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'p' });
  const pageWidthPt = doc.internal.pageSize.getWidth();
  const pageHeightPt = doc.internal.pageSize.getHeight();

  for (let i = 0; i < pages.length; i++) {
    if (i > 0) doc.addPage('a4', 'p');
    const dataUrl = await blobToDataUrl(pages[i].blob);
    doc.addImage(dataUrl, 'JPEG', 0, 0, pageWidthPt, pageHeightPt, undefined, 'FAST');
  }

  doc.save(filename);
}

/**
 * Trigger one browser download per generated page. Chrome / Firefox prompt
 * the user once to allow multiple downloads from the same origin and then
 * accept the rest silently. Spacing them out with a small interval avoids
 * the browser collapsing rapid back-to-back triggers.
 */
export function downloadImagesSequentially(
  pages: GeneratedPage[],
  prefix: string
): void {
  pages.forEach((page, idx) => {
    setTimeout(() => {
      const url = URL.createObjectURL(page.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${prefix}-P${String(page.pageNumber).padStart(3, '0')}.jpg`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Keep the URL alive briefly so the download stream can finish before
      // the GC reclaims the blob.
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    }, idx * 120);
  });
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}
